import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { Rfq, RfqStatus } from './domain'

/**
 * UID-586 — polls `GET /rfq/{rfqId}` to detect the `requested`/`quoting` ->
 * `quoted` transition WITHOUT a page reload/remount, and to pick up the
 * real, server-priced `Rfq` once it lands (the summary rail needs the whole
 * object, not just `status` — see `RfqReviewPage.tsx`'s swap to
 * `RfqDetailSummaryRail` once quoted). Nothing else in this app polls
 * (`useHydrateRfqCount.ts`'s own comment: "nothing in this app polls — it
 * only refires on navigation") — this is deliberately the first, scoped to
 * the one screen that now needs to observe an async backend transition
 * (`auto-quote-runner.ts`'s deferred quoting pass) while staying mounted.
 */
const POLL_INTERVAL_MS = 4000

function isTerminalStatus(status: RfqStatus): boolean {
  return (
    status === 'quoted' || status === 'accepted' || status === 'declined' || status === 'expired'
  )
}

/**
 * `initialRfq` is `undefined` until a submit succeeds — the hook does
 * nothing (no request, no interval) until then. `initialRfq` is the
 * `POST /rfq/{rfqId}/submit` response itself (already a full `RfqView`), so
 * there is no extra fetch just to seed this. Once a terminal status is
 * reached, polling stops permanently for that RFQ; a NEW `initialRfq.id`
 * (a different RFQ) restarts it, keyed by the effect's own deps.
 */
export function useRfqSubmissionStatus(initialRfq: Rfq | undefined): Rfq | undefined {
  const [rfq, setRfq] = useState<Rfq | undefined>(initialRfq)
  const rfqRef = useRef(rfq)
  rfqRef.current = rfq

  useEffect(() => {
    setRfq(initialRfq)
  }, [initialRfq])

  const rfqId = initialRfq?.id
  const initialStatus = initialRfq?.status

  useEffect(() => {
    if (rfqId === undefined || initialStatus === undefined || isTerminalStatus(initialStatus)) {
      return
    }
    let cancelled = false

    const poll = async () => {
      if (cancelled || (rfqRef.current !== undefined && isTerminalStatus(rfqRef.current.status))) {
        return
      }
      const { data } = await apiClient.GET('/rfq/{rfqId}', { params: { path: { rfqId } } })
      if (!cancelled && data) {
        setRfq(data)
      }
    }

    const interval = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [rfqId, initialStatus])

  return rfq
}
